import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Drawer,
  IconButton,
  useTheme,
  Theme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppTablePagenation from "../../../../../views/components/common/pages/AppTablePagenation";
import { formatDate, formatDateWithoutTime } from "../../../../../views/components/common/functions/formatDate";
import { useStores } from "../../../use-stores";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { PurchaseRequestActivityLog } from "../../../../../models/PurchaseRequest/PurchaseRequestActivityLog";
import {
  getModernPageColors,
  modernDrawerHeaderSx,
  modernEmptyStateSx,
} from "../../../../../styles/modernListingPageSx";

interface PurchaseRequestActivityLogsDrawerProps {
  open: boolean;
  onClose: () => void;
  prId: number | null;
}

/** Above AppBar (`theme.zIndex.drawer + 1`). */
const ACTIVITY_LOGS_DRAWER_Z_OFFSET = 2000;

const displayChangeValue = (value: string | number | null): string => {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
};

const groupLogsByDate = (
  logs: PurchaseRequestActivityLog[],
): Record<string, PurchaseRequestActivityLog[]> => {
  const grouped: Record<string, PurchaseRequestActivityLog[]> = {};
  logs.forEach((log) => {
    const dateKey = formatDateWithoutTime(log.created_date, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    if (!grouped[dateKey]) grouped[dateKey] = [];
    grouped[dateKey].push(log);
  });
  return grouped;
};

const getInitials = (name: string | null): string => {
  const trimmed = name?.trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(" ").filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return trimmed[0].toUpperCase();
};

const AVATAR_COLORS = [
  "#1976d2",
  "#388e3c",
  "#d32f2f",
  "#7b1fa2",
  "#f57c00",
  "#0288d1",
  "#689f38",
  "#e64a19",
];

const getAvatarColor = (name: string | null): string => {
  const trimmed = name?.trim() || "?";
  let hash = 0;
  for (let i = 0; i < trimmed.length; i++) {
    hash = trimmed.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const ActivityLogEntry: React.FC<{ log: PurchaseRequestActivityLog }> = ({
  log,
}) => {
  const theme = useTheme<Theme>();
  const colors = getModernPageColors(theme);
  const [expanded, setExpanded] = useState(false);
  const hasChangeDetails = Boolean(log.change_details?.length);

  return (
    <Box sx={{ display: "flex", gap: 1.25 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "0.7rem",
          fontWeight: 700,
          color: "#fff",
          backgroundColor: getAvatarColor(log.admin_name),
        }}
      >
        {getInitials(log.admin_name)}
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          border: `1px solid ${colors.border}`,
          borderRadius: 2,
          p: 1.5,
          bgcolor: colors.surface,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            flexWrap: "wrap",
            mb: 0.75,
          }}
        >
          <Chip
            label={log.action_label}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: "0.7rem" }}
          />
          <CustomTypography
            variant="body2"
            sx={{ color: colors.subtitle, fontSize: "0.72rem" }}
          >
            {formatDate(log.created_date)}
          </CustomTypography>
        </Box>

        <CustomTypography
          variant="body2"
          sx={{ color: colors.title, lineHeight: 1.5, wordBreak: "break-word" }}
        >
          {log.summary}
        </CustomTypography>

        {hasChangeDetails && (
          <>
            <Box
              onClick={() => setExpanded((prev) => !prev)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                mt: 1,
                cursor: "pointer",
                color: colors.accent,
                width: "fit-content",
              }}
            >
              <CustomTypography
                variant="body2"
                sx={{ fontSize: "0.72rem", fontWeight: 600, color: "inherit" }}
              >
                {expanded ? "Hide changes" : "View changes"}
              </CustomTypography>
              {expanded ? (
                <ExpandLessIcon sx={{ fontSize: 16 }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: 16 }} />
              )}
            </Box>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box
                sx={{
                  mt: 1,
                  borderRadius: 1.5,
                  bgcolor: colors.surfaceSoft,
                  p: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                }}
              >
                {log.change_details?.map((change, index) => (
                  <Box
                    key={`${log.id}-${change.field}-${index}`}
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.5,
                      fontSize: "0.72rem",
                    }}
                  >
                    <CustomTypography
                      variant="body2"
                      sx={{ fontWeight: 600, color: colors.title, fontSize: "0.72rem" }}
                    >
                      {change.label}:
                    </CustomTypography>
                    <CustomTypography
                      variant="body2"
                      sx={{ color: colors.subtitle, fontSize: "0.72rem" }}
                    >
                      {displayChangeValue(change.from)} → {displayChangeValue(change.to)}
                    </CustomTypography>
                  </Box>
                ))}
              </Box>
            </Collapse>
          </>
        )}
      </Box>
    </Box>
  );
};

const PurchaseRequestActivityLogsDrawer: React.FC<
  PurchaseRequestActivityLogsDrawerProps
> = ({ open, onClose, prId }) => {
  const theme = useTheme<Theme>();
  const colors = getModernPageColors(theme);
  const { purchaseRequestStore } = useStores();
  const { showError } = useNotification();

  useEffect(() => {
    if (!open || !prId) return;
    purchaseRequestStore
      .getPurchaseRequestActivityLogs(prId, 1)
      .catch((error: any) => {
        showError(
          "Error",
          error?.response?.data?.message ||
            "Failed to fetch activity logs. Please try again later.",
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prId]);

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => {
    if (!prId) return;
    purchaseRequestStore
      .getPurchaseRequestActivityLogs(prId, page)
      .catch((error: any) => {
        showError(
          "Error",
          error?.response?.data?.message ||
            "Failed to fetch activity logs. Please try again later.",
        );
      });
  };

  const groupedLogs = groupLogsByDate(purchaseRequestStore.activityLogs);
  const sortedDates = Object.keys(groupedLogs).sort(
    (a, b) => new Date(b).getTime() - new Date(a).getTime(),
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 460, md: 520 },
          maxWidth: { xs: "100vw", sm: 520 },
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
      ModalProps={{
        keepMounted: true,
        sx: {
          zIndex: (muiTheme) =>
            muiTheme.zIndex.drawer + ACTIVITY_LOGS_DRAWER_Z_OFFSET,
        },
        slotProps: {
          backdrop: {
            sx: {
              zIndex: (muiTheme) =>
                muiTheme.zIndex.drawer + ACTIVITY_LOGS_DRAWER_Z_OFFSET - 1,
            },
          },
        },
      }}
    >
      <Box sx={modernDrawerHeaderSx(theme)}>
        <Box display="flex" gap={1.5} alignItems="center" minWidth={0}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "10px",
              backgroundColor: theme.palette.primary.main,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <HistoryIcon sx={{ color: "white", fontSize: 22 }} />
          </Box>
          <Box minWidth={0}>
            <CustomTypography variant="heading5" sx={{ color: colors.title }}>
              Activity Logs
            </CustomTypography>
            <CustomTypography
              variant="body2"
              sx={{
                mt: 0.5,
                color: colors.subtitle,
                fontSize: { xs: "0.8rem", sm: "0.875rem" },
              }}
            >
              {prId ? `Purchase Request #${prId}` : "Purchase request history"}
            </CustomTypography>
          </Box>
        </Box>

        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          px: { xs: 1.5, sm: 2.5 },
          py: { xs: 1.5, sm: 2 },
        }}
      >
        {purchaseRequestStore.activityLogsLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 240,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        ) : purchaseRequestStore.activityLogs.length === 0 ? (
          <Box sx={{ ...modernEmptyStateSx(theme), minHeight: 240 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <HistoryIcon sx={{ fontSize: 44, opacity: 0.5 }} />
              <CustomTypography variant="body1">
                No activity recorded for this purchase request yet.
              </CustomTypography>
            </Box>
          </Box>
        ) : (
          sortedDates.map((date) => (
            <Box key={date} sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 2,
                }}
              >
                <Box sx={{ height: "1px", flex: 1, backgroundColor: colors.border }} />
                <CustomTypography
                  variant="body2"
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    fontWeight: 600,
                    fontSize: "0.72rem",
                    backgroundColor: colors.headerBg,
                    borderRadius: "12px",
                    color: colors.subtitle,
                  }}
                >
                  {date}
                </CustomTypography>
                <Box sx={{ height: "1px", flex: 1, backgroundColor: colors.border }} />
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {groupedLogs[date].map((log) => (
                  <ActivityLogEntry key={log.id} log={log} />
                ))}
              </Box>
            </Box>
          ))
        )}
      </Box>

      {purchaseRequestStore.activityLogsTotalCount > 0 && (
        <Box sx={{ borderTop: `1px solid ${colors.border}` }}>
          <AppTablePagenation
            fontSize="0.75rem"
            pageNo={purchaseRequestStore.activityLogsPageNo}
            limit={purchaseRequestStore.activityLogsLimit}
            totalCount={purchaseRequestStore.activityLogsTotalCount}
            showRowsPerPageOptions={false}
            onPageChange={handlePageChange}
            handleRowsChange={() => {}}
          />
        </Box>
      )}
    </Drawer>
  );
};

export default observer(PurchaseRequestActivityLogsDrawer);
